import axios, { AxiosError } from "axios";
import supabase from "./supabase";

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
});

apiClient.interceptors.request.use(async (config) => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // On 401, try refreshing the session once and retry
    if (error?.response?.status === 401 && !originalRequest._retried) {
      originalRequest._retried = true;
      const {
        data: { session },
      } = await supabase.auth.refreshSession();
      if (session?.access_token) {
        originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
        return apiClient(originalRequest);
      }
    }

    const status = error?.response?.status ?? 0;
    const message =
      error?.response?.data?.message || error?.message || "Unknown error";
    const data = error?.response?.data?.data ?? null;

    // Custom normalized error
    return Promise.reject({
      status,
      message,
      data,
      raw: error, // keep original if needed
    });
  },
);

export interface HttpError {
  status: number;
  message: string;
  data: Record<string, unknown> | null;
  raw: AxiosError;
}

export const isHttpError = (err: unknown): err is HttpError => {
  return (
    typeof err === "object" && err !== null && "status" in err && "raw" in err
  );
};

export default apiClient;
